import { Box } from '@cp/ds/src/components/box';
import { Breadcrumbs } from '@cp/ds/src/components/breadcrumbs';
import { Container } from '@cp/ds/src/components/container';
import { Stack } from '@cp/ds/src/components/stack';
import { StyledListItem } from '@cp/ds/src/components/styled/styledListItem';
import { Typography } from '@cp/ds/src/components/typography';
import React, { PropsWithChildren } from 'react';
import { usePathGenerator } from '@cp/entities/clientWebRoutes';
import { ClientWebPage } from '@common/model/meta/clientWebPage';
import { styled } from '@cp/ds/src/theme';
import { RoutedLink as Link, RoutedLink } from 'src/shared/ui/link';

const QuestionList = styled('ol')(({ theme }) => ({
  paddingInlineStart: 0,
  marginBlockStart: theme.spacing(2),
  marginBlockEnd: theme.spacing(2),
}));

const Question = (props: PropsWithChildren) => (
  <StyledListItem itemProp="mainEntity" itemScope itemType="https://schema.org/Question" {...props} />
);
const Answer = (props: PropsWithChildren) => (
  <div itemProp="acceptedAnswer" itemScope itemType="https://schema.org/Answer">
    <div itemProp="text">{props.children}</div>
  </div>
);

export const SiteFaq = () => {
  const generateAppPath = usePathGenerator();
  return (
    <Container>
      <Breadcrumbs
        LinkComponent={RoutedLink}
        items={[
          {
            path: '/',
            label: 'Главная',
          },
          {
            path: '',
            label: 'Вопросы и ответы',
          },
        ]}
        sx={{ my: 2 }}
      />
      <Stack itemScope itemType="https://schema.org/FAQPage" sx={{ mb: { xs: 4, md: 8 } }}>
        <Typography component="h1" sx={{ typography: { xs: 'h4', md: 'h1' }, m: 0, mb: { xs: 3, md: 5 } }}>
          Вопросы и ответы
        </Typography>
        <Box component="section" id="client-faq">
          <Typography component="h3" sx={{ typography: { xs: 'h6', md: 'h3' }, m: 0, mb: { xs: 2.5 } }}>
            Ответы на вопросы нанимателей:
          </Typography>
          <QuestionList>
            <Question>
              <b itemProp="name">1) Что такое YouHelp?</b>
              <Answer>
                <Typography component="p" sx={{ my: 2 }}>
                  YouHelp — не агентство. Это Online-сервис, который помогает студентам и не только найти полезную подработку, а родителям —
                  быстро найти няню.
                </Typography>
              </Answer>
            </Question>
            <Question>
              <b itemProp="name">2) Кто такой бебиситтер?</b>
              <Answer>
                <Typography component="p" sx={{ my: 2 }}>
                  Бебиситтеры — почасовые помощники, чаще всего молодые девушки и юноши, нередко студенты. Они легко находят общий язык с
                  детьми, любят играть, рисовать, танцевать, устраивать домашние спектакли и с радостью ползают по полу. Им можно доверить
                  забрать ребёнка из школы, сопроводить на кружки, поиграть до возвращения родителей. Они работают неполный день, не ищут
                  постоянной занятости, но готовы регулярно заниматься с одним ребёнком. В отличие от профессиональных нянь, у них масса
                  интересов — и это делает общение с ними живым и увлекательным. Вместо бесконечных поисков "няня на час СПБ" или "почасовая
                  няня МСК" вы просто знаете, к кому обратиться.
                </Typography>
              </Answer>
            </Question>
            <Question>
              <b itemProp="name">3) Что я получу после регистрации на сайте YouHelp?</b>
              <Answer>
                <Typography component="p" sx={{ my: 2 }}>
                  После регистрации на сайте вы получите доступ ко всей базе нянь, сиделок, домработниц и репетиторов YouHelp. Вы сможете
                  находить свободных помощников на удобное вам время, просматривать анкеты и отзывы, а также размещать объявления и откликаться
                  на кандидатов. Все основные функции сервиса доступны бесплатно — вы можете пользоваться платформой без ограничений и
                  посредников.
                </Typography>
              </Answer>
            </Question>
            <Question>
              <b itemProp="name">4) Какие гарантии вы предоставляете?</b>
              <Answer>
                <Typography component="p" sx={{ my: 2 }}>
                  YouHelp — это информационный сервис, мы не агентство. Вы сами выбираете помощника, а мы лишь рекомендуем. Например,
                  советуем обращать внимание на бебиситтеров, прошедших школу YouHelp — их легко узнать по значку с "школа YouHelp". Каждого
                  ситтера мы проверяем: сначала изучаем его страницы в соцсетях, затем проводим интервью по телефону, после этого — обучение
                  и тестирование под наблюдением психолога. Надёжность документов проверяется через сервис SpectrumData. После работы с
                  няней родители могут оставить отзыв — на основе оценок и количества заказов формируется рейтинг. Но окончательный выбор
                  всегда остаётся за вами.
                </Typography>
              </Answer>
            </Question>
            <Question>
              <b itemProp="name">5) Что делают помощники?</b>
              <Answer>
                <Typography component="p" sx={{ my: 2 }}>
                  Няни делают то, что важно для каждой конкретной семьи.
                  <br />
                  У всех свои привычки, правила и подход к воспитанию — и мы это уважаем. Поэтому бебиситтеры не вмешиваются в уклад, а
                  аккуратно встраиваются в него. Их приглашают поиграть с ребёнком, погулять, забрать из школы, сводить на кружок, сходить в
                  музей или даже поехать вместе в отпуск.
                </Typography>
              </Answer>
            </Question>
            <Question>
              <b itemProp="name">6) Зачем мне это нужно?</b>
              <Answer>
                <Typography component="p" sx={{ my: 2 }}>
                  Няня заболела в самый неподходящий момент? Ребёнка надо отвести на занятия? Провести время вдвоем без детей? Поспать пару
                  часов днем, пока дети гуляют? Сделать маникюр или сходить в кино? Погулять вечером, когда дети заснули? Да просто помочь
                  вам с детьми в любое время — для этого и есть YouHelp.
                </Typography>
              </Answer>
            </Question>
            <Question>
              <b itemProp="name">7) Как связаться со специалистом?</b>
              <Answer>
                <Typography component="p" sx={{ my: 2 }}>
                  Чтобы связаться со специалистом, необходимо зарегистрироваться и войти в свой аккаунт. Вы сможете написать кандидату в чат
                  сервиса или откликнуться на его анкету.
                </Typography>
              </Answer>
            </Question>
            <Question>
              <b itemProp="name">8) В чем ваши преимущества?</b>
              <Answer>
                <Typography component="ul" sx={{ my: 2 }}>
                  <Typography component="li">специалисты проверены и обучены в Академии Нянь YouHelp;</Typography>
                  <Typography component="li">реализован функционал поиска специалистов на карте;</Typography>
                  <Typography component="li">техническая поддержка, которая оперативно ответит на все вопросы;</Typography>
                  <Typography component="li">
                    мобильные приложения AppStore & Google Play для того, чтобы оперативно получать уведомления, общаясь с работниками.
                  </Typography>
                </Typography>
              </Answer>
            </Question>
            <Question>
              <b itemProp="name">9) Как разместить вакансию?</b>
              <Answer>
                <Typography component="p" sx={{ my: 2 }}>
                  Вы можете самостоятельно перейти в раздел меню «Объявления» и нажать кнопку «Добавить объявление», выбрать специальность
                  кандидата и заполнить все разделы по вакансии. Также Вы можете воспользоваться функцией «Я не хочу выбирать» — в таком
                  случае заказ будет оформлен автоматически, и мы выберем наиболее подходящую няню из откликнувшихся по цене не выше
                  указанной; при этом номер телефона ситтера станет виден в чате YouHelp.
                </Typography>
              </Answer>
            </Question>
            <Question>
              <b itemProp="name">10) Как удалить вакансию?</b>
              <Answer>
                <Typography component="p" sx={{ my: 2 }}>
                  Вы можете удалить вакансию в разделе меню «Мои объявления».
                </Typography>
              </Answer>
            </Question>
            <Question>
              <b itemProp="name">11) Можно ли вносить изменения в свою информацию после создания учетной записи?</b>
              <Answer>
                <Typography component="p" sx={{ my: 2 }}>
                  Да, можно менять все данные, кроме номера телефона. В этом случае будет необходимо пройти регистрацию заново.
                </Typography>
              </Answer>
            </Question>
            <Question>
              <b itemProp="name">12) Как зарегистрироваться нанимателем?</b>
              <Answer>
                <Typography component="p" sx={{ my: 2 }}>
                  Нажмите кнопку «Вход» в верхней части сайта, введите ваш номер телефона или электронную почту и подтвердите четырехзначным
                  кодом из SMS или из письма на почте, далее выберите «Я ищу помощника», а далее введите город, адрес, имя. ВАЖНО: мы не делаем SMS-рассылки и негативно относимся к
                  спаму на телефон, поэтому ваши данные полностью защищены и хранятся в специальной изолированной базе данных на выделенном
                  сервере.
                </Typography>
              </Answer>
            </Question>
            <Question>
              <b itemProp="name">13) Регистрация на сайте завершена, что делать дальше?</b>
              <Answer>
                <Typography component="p" sx={{ my: 2 }}>
                  <b>Создайте вакансию</b>
                  <br />
                  Зайдите в раздел «Мои объявления» и создайте вакансию о поиске кандидата. Соискатели, которые заинтересованы в
                  сотрудничестве, откликнутся на ваше объявление. Это ускорит процесс подбора, а также поможет получить отклики от
                  кандидатов, которых вы не найдёте в каталоге (некоторые соискатели скрывают свои анкеты и откликаются только на
                  понравившиеся им вакансии).
                </Typography>
                <Typography component="p" sx={{ my: 2 }}>
                  <b>Используйте поиск</b>
                  <br />В разделе «Помощники» отображены все работники, которые зарегистрированы на сайте (кроме тех, кто скрыл свои
                  резюме). Для целевого поиска установите в фильтрах критерии, которые для вас важны (стоимость за час, количество детей,
                  преимущества и дополнительные критерии). Система отобразит списком кандидатов, которые соответствуют указанным параметрам.
                </Typography>
              </Answer>
            </Question>
            <Question>
              <b itemProp="name">14) Что эффективнее: разместить объявление или использовать общий поиск?</b>
              <Answer>
                <Typography component="p" sx={{ my: 2 }}>
                  Мы искренне советуем Вам и публиковать вакансию и пользоваться поиском! Размещая вакансию, вы получаете отклики от тех
                  кандидатов, которые уже заинтересованы в сотрудничестве (в том числе отклики от работников, которые держат свои анкеты в
                  закрытом режиме и в общем поиске не видны). Самостоятельный поиск поможет Вам найти кандидатов, которые соответствуют всем
                  вашим параметрам. Получая и отклики на объявление и выбирая самостоятельно по каталогу, вы сэкономите время на поиск
                  нужного кандидата.
                </Typography>
              </Answer>
            </Question>
            <Question>
              <b itemProp="name">15) Как мне оставить отзыв на работника?</b>
              <Answer>
                <Typography component="p" sx={{ my: 2 }}>
                  Если Вы хотите оставить отзыв или дать рекомендацию о работнике, пожалуйста, напишите нам. Мы обязательно учтем эти данные
                  при формировании рейтинга соискателя.
                </Typography>
              </Answer>
            </Question>
            <Question>
              <b itemProp="name">16) Кто увидит мои контактные данные (мобильный телефон и электронную почту)?</b>
              <Answer>
                <Typography component="p" sx={{ my: 2 }}>
                  Ваши контакты по умолчанию скрыты на сайте от работников. Соискатели могут связаться с вами по номеру телефона или через
                  WhatsApp после отклика на размещённую вами вакансию.
                </Typography>
              </Answer>
            </Question>
            <Question>
              <b itemProp="name">17) Как удалить свой аккаунт?</b>
              <Answer>
                <Typography component="p" sx={{ my: 2 }}>
                  Вы уверены, что хотите удалить свой аккаунт? У Вас исчезнут ваши вакансии, отклики соискателей и вся история
                  взаимодействия. Если вы уже нашли работника или вдруг передумали его искать, то можете просто удалить вашу вакансию. Если
                  вы уверены, что хотите удалить свой аккаунт, обратитесь в службу техподдержки.
                </Typography>
              </Answer>
            </Question>
          </QuestionList>
        </Box>
        <Box component="section" id="helper-faq">
          <Typography component="h3" sx={{ typography: { xs: 'h6', md: 'h3' }, mb: { xs: 2.5 } }}>
            Ответы на вопросы работников:
          </Typography>
          <QuestionList>
            <Question>
              <b itemProp="name">1) Можно ли зарегистрировать на сайте родных или друзей и искать для них работу?</b>
              <Answer>
                <Typography component="p" sx={{ my: 2 }}>
                  Для поиска работы у нас на сервисе кандидату необходимо зарегистрироваться самостоятельно по номеру телефона или
                  электронной почте, а также:
                  <br />
                  - создать анкету;
                  <br />
                  - активно откликаться на вакансии (объявления).
                  <br />
                  Желательно, делать не менее 5-ти откликов в день. Это поможет быстрее найти достойную работу.
                </Typography>
              </Answer>
            </Question>
            <Question>
              <b itemProp="name">2) Почему мне не звонят и не пишут наниматели? Как увеличить количество звонков и сообщений?</b>
              <Answer>
                <Typography component="p" sx={{ my: 2 }}>
                  Чтобы получать больше сообщений и звонков, ваша анкета должна быть конкурентоспособной. Для этого Вам нужно:
                </Typography>
                <Typography component="ul" sx={{ my: 2 }}>
                  <Typography component="li">загрузить Вашу фотографию в хорошем качестве согласно инструкции;</Typography>
                  <Typography component="li">максимально подробно описать ваш опыт и навыки в анкете;</Typography>
                  <Typography component="li">
                    пройти обучение в Академии Нянь YouHelp - работодатели больше доверяют и ставят в приоритет обученных соискателей.
                  </Typography>
                  <Typography component="li">пройти проверку документов. </Typography>
                </Typography>
                <Typography component="p" sx={{ my: 2 }}>
                  Не стоит ждать, когда Вам напишут или позвонят наниматели. Как можно чаще откликайтесь на понравившиеся объявления.
                  Просматривайте новые вакансии на сайте. Это поможет Вам быстрее найти хорошую работу!
                </Typography>
              </Answer>
            </Question>
            <Question>
              <b itemProp="name">3) Как связаться с нанимателем?</b>
              <Answer>
                <Typography component="p" sx={{ my: 2 }}>
                  Вы можете связаться с нанимателем напрямую через чат сервиса.
                </Typography>
              </Answer>
            </Question>
            <Question>
              <b itemProp="name">4) Как мне понять, просмотрел наниматель мой отклик на вакансию или нет?</b>
              <Answer>
                <Typography component="p" sx={{ my: 2 }}>
                  Наниматель может просмотреть сотни откликов и так и не выйти на связь, если его не устраивает уровень соискателей,
                  например, или же территориальная составляющая (кандидат слишком далеко проживает от нанимателя). Если Вы подходите
                  работодателю, он ответит Вам в чате сервиса или Вы можете уточнить детали заказа напрямую, написав в мессенджер или
                  позвонив по телефону, указанному в вакансии.
                </Typography>
              </Answer>
            </Question>
            <Question>
              <b itemProp="name">5) Что делать, если я переезжаю в другой регион?</b>
              <Answer>
                <Typography component="p" sx={{ my: 2 }}>
                  При переезде в другой регион вы можете поменять город и адрес проживания в разделе «Обо мне» вашего профиля. Если вы
                  планируете отъезд на некоторое время, то советуем по возможности регулярно заходить в приложение, отвечать на личные
                  сообщения и быть на связи.
                </Typography>
              </Answer>
            </Question>
            <Question>
              <b itemProp="name">6) На каком основании вы запрашиваете мои персональные данные?</b>
              <Answer>
                <Typography component="p" sx={{ my: 2 }}>
                  YouHelp.ru зарегистрирован в реестре операторов персональных данных. Мы строго соблюдаем требования действующего
                  законодательства и собственную «Политику в отношении обработки персональных данных». Указание фамилии, имени, даты
                  рождения, адреса, образования, гражданства и иных персональных данных позволит Вам быстрее найти достойную работу.
                </Typography>
              </Answer>
            </Question>
            <Question>
              <b itemProp="name">7) Как удалить аккаунт?</b>
              <Answer>
                <Typography component="p" sx={{ my: 2 }}>
                  Вы уверены, что хотите удалить свой аккаунт? У Вас исчезнут все данные, которые вы заполняли, и вся история взаимодействия
                  (переписка с работодателями, отклики и т.д.). Если вы уже нашли работу, то можете скрыть анкету из поиска, выключив кнопку
                  «Работаю» в профиле. Если вы уверены, что хотите удалить свой аккаунт, обратитесь в службу техподдержки.
                </Typography>
              </Answer>
            </Question>
          </QuestionList>
          <Typography component="p" sx={{ my: 2 }}>
            Не нашли ответа на ваш вопрос? Спросите через{' '}
            <Link href={generateAppPath({ page: ClientWebPage.Contacts })}>форму обратной связи.</Link>
            <br />
            <b>Недовольны сайтом, клиентским обслуживанием, возникла проблема?</b> —{' '}
            <Link href={generateAppPath({ page: ClientWebPage.WriteToDirector })}>напишите лично генеральному директору.</Link>
          </Typography>
        </Box>
      </Stack>
    </Container>
  );
};
